/**
 * UsersTable component for displaying user list in admin interface
 * Feature: 001-user-role-management (Phase 4 - T064, Phase 5 - T092)
 */
import { UserOrInvitation } from '../../types/user';
import { RefreshCw, Ban, Unlock, Mail, Trash2 } from 'lucide-react';
import { UserRoleSelect } from './UserRoleSelect';

interface UsersTableProps {
  users: UserOrInvitation[];
  onSyncUser: (userId: string) => void;
  onUpdateStatus: (userId: string, status: 'active' | 'blocked') => void;
  onDeleteUser: (userId: string, email: string) => void;
  isLoading?: boolean;
}

export function UsersTable({
  users,
  onSyncUser,
  onUpdateStatus,
  onDeleteUser,
  isLoading = false,
}: UsersTableProps) {
  const getSyncStatusBadge = (status: string) => {
    const badges = {
      invited: 'bg-brand-primary/10 text-brand-primary',
      synced: 'bg-brand-teal/10 text-brand-teal',
      pending: 'bg-brand-warning/10 text-brand-warning',
      failed: 'bg-brand-error/10 text-brand-error',
      manual: 'bg-brand-teal/10 text-brand-teal',
    };
    return badges[status as keyof typeof badges] || 'bg-brand-surface text-brand-text';
  };

  // Use Clerk's banned status as source of truth
  const isUserBlocked = (user: UserOrInvitation) => {
    return user.is_blocked === true;
  };

  const getRoleBadge = (role: string) => {
    return role === 'admin'
      ? 'bg-brand-primary/10 text-brand-primary'
      : 'bg-brand-surface text-brand-text';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-pill h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-brand-muted">
        No users found
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-brand-border">
        <thead className="bg-brand-surface">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
              User
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
              Role
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
              Sync Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider">
              Last Synced
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-brand-muted uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-brand-bg divide-y divide-brand-border">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-brand-surface">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className={`h-10 w-10 rounded-pill flex items-center justify-center ${
                      user.sync_status === 'invited' ? 'bg-brand-primary/20' : 'bg-brand-surface'
                    }`}>
                      {user.sync_status === 'invited' ? (
                        <Mail className="h-5 w-5 text-brand-primary" />
                      ) : (
                        <span className="text-sm font-medium text-brand-muted">
                          {user.display_name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-brand-text">
                      {user.sync_status === 'invited' ? (
                        <span className="italic text-brand-muted">Pending Invitation</span>
                      ) : (
                        user.display_name || 'Unknown'
                      )}
                    </div>
                    <div className="text-sm text-brand-muted">
                      {user.clerk_id || 'Not synced'}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-brand-text">{user.email}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {user.sync_status === 'invited' ? (
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-pill ${getRoleBadge(user.role)}`}>
                    {user.role}
                  </span>
                ) : (
                  <UserRoleSelect user={user} />
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-pill ${getSyncStatusBadge(user.sync_status)}`}>
                    {user.sync_status}
                  </span>
                  {isUserBlocked(user) && (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-pill bg-brand-error/10 text-brand-error">
                      Blocked
                    </span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                {user.sync_status === 'invited' ? (
                  user.invited_at ? new Date(user.invited_at).toLocaleDateString() : 'Pending'
                ) : (
                  user.last_synced_at ? new Date(user.last_synced_at).toLocaleDateString() : 'Never'
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                {user.sync_status === 'invited' ? (
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-xs text-brand-muted italic">Awaiting acceptance</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onSyncUser(user.id)}
                      className="text-brand-teal hover:opacity-70 p-1 hover:bg-brand-surface rounded"
                      title="Sync user"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    {isUserBlocked(user) ? (
                      <button
                        onClick={() => onUpdateStatus(user.id, 'active')}
                        className="text-brand-teal hover:opacity-70 p-1 hover:bg-brand-surface rounded"
                        title="Unblock user"
                      >
                        <Unlock className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => onUpdateStatus(user.id, 'blocked')}
                        className="text-brand-warning hover:opacity-70 p-1 hover:bg-brand-surface rounded"
                        title="Block user"
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onDeleteUser(user.id, user.email)}
                      className="text-brand-error hover:opacity-70 p-1 hover:bg-brand-surface rounded"
                      title="Delete user"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
